/**
 * WaitTimesPageNew — full feature parity with old WaitTimesPage.
 * Uses useAdminAttractions (server-side data) like the old page.
 * Includes: park/type/sort filters, freshness indicator, park legend, grouped results.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAttractions, type AdminAttraction } from '../hooks/useAdminAttractions';
import { useParkRank } from '../hooks/useParkRank';
import { useEntityTypeOptions } from '../hooks/useAdminAttractions';
import { useParks } from '../hooks/useParks';
import { PriorityBadge } from '../design-system/primitives/PriorityBadge';
import { EntityTypePill } from '../design-system/primitives/EntityTypePill';
import { WaitTimeIndicator } from '../design-system/primitives/WaitTimeIndicator';
import { EmptyState } from '../components/shared/EmptyState';
import { ParkSelector } from '../components/shared/ParkSelector';
import { Star, Search, SlidersHorizontal } from 'lucide-react';
import { getParkIcon, getParkAbbr } from '../config/parkIcons';

function AttractionFreshness({ minutesSinceUpdate }: { minutesSinceUpdate: number | null }) {
  const label = minutesSinceUpdate == null ? '—' : minutesSinceUpdate === 0 ? 'just now' : `${minutesSinceUpdate}m ago`;
  return (
    <span className="w-16 text-right text-xs text-gray-400 dark:text-gray-500 shrink-0" title="Data last updated">
      {label}
    </span>
  );
}

function StatusPill({ status }: { status: string | null }): React.ReactElement {
  const color = status === 'Operating' ? 'bg-disney-green-100 dark:bg-disney-green-900 text-disney-green-700 dark:text-disney-green-300'
    : status === 'Closed' ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300'
    : status === 'Downtime' ? 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300'
    : status === 'Temporarily Closed' ? 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300'
    : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400';
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {status ?? '—'}
    </span>
  );
}

export function WaitTimesPageNew(): React.ReactElement {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _navigate = useNavigate();
  const [parkFilter, setParkFilter] = React.useState('');
  const [typeFilter, setTypeFilter] = React.useState('');
  const [sortBy, setSortBy] = React.useState<'priority' | 'rating' | 'name'>('priority');
  const [filtersOpen, setFiltersOpen] = React.useState(false);

  const entityTypeOptions = useEntityTypeOptions();
  const { data: parks } = useParks();
  const { data: attractions, isLoading } = useAdminAttractions({
    parkName: parkFilter || undefined,
    entityType: typeFilter || undefined,
    sortBy,
  });

  // Park-wide rank from raw (unfiltered) attractions for composite status
  const { ranks: parkRanks, operatingGroups } = useParkRank(attractions ?? null);

  const activeFilterCount = (typeFilter ? 1 : 0) + (sortBy !== 'priority' ? 1 : 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Wait Times</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">Browse all attractions across the parks</p>
      </div>

      {/* Top controls: park dropdown + filter toggle */}
      <div className="flex items-center gap-2">
        {/* Park dropdown */}
        <ParkSelector
          parks={parks ?? []}
          value={parkFilter}
          onChange={setParkFilter}
          placeholder="All parks"
          includeAll
          allLabel="All parks"
        />

        {/* Filters toggle */}
        <button
          onClick={() => setFiltersOpen((o) => !o)}
          className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border transition-colors ${filtersOpen ? 'bg-disney-blue-500 text-white border-disney-blue-500' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-dark-card text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
          aria-expanded={filtersOpen}
        >
          <SlidersHorizontal className="w-4 h-4" />
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <span className={`text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center ${filtersOpen ? 'bg-white/30 text-white' : 'bg-disney-blue-500 text-white'}`}>
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Collapsible filters panel */}
      {filtersOpen && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-card p-3 space-y-3">
          {/* Type filter */}
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium">Type</p>
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => setTypeFilter('')}
                className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${typeFilter === '' ? 'bg-disney-blue-500 text-white border-disney-blue-500' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                All types
              </button>
              {entityTypeOptions.filter((opt) => opt.value !== '').map((opt) => (
                <button key={opt.value} onClick={() => setTypeFilter(opt.value)}
                  className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${typeFilter === opt.value ? 'bg-disney-blue-500 text-white border-disney-blue-500' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Sort */}
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium">Sort by</p>
            <div className="flex flex-wrap gap-1.5">
              {([
                { key: 'priority' as const, label: 'Priority' },
                { key: 'rating' as const, label: 'Rating' },
                { key: 'name' as const, label: 'Name' },
              ]).map((option) => (
                <button key={option.key} onClick={() => setSortBy(option.key)}
                  className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${sortBy === option.key ? 'bg-disney-blue-500 text-white border-disney-blue-500' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-card overflow-hidden">
        {/* Results header */}
        <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700/50 bg-gray-50 dark:bg-gray-900/50 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
            {attractions?.length != null ? `${attractions.length} attractions` : 'Loading...'}
          </span>
        </div>

        {/* List */}
        <div className="max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <>
              {/* Mobile skeleton cards */}
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="md:hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-card p-3 space-y-2 animate-pulse">
                  <div className="w-28 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
                  <div className="w-48 h-3 bg-gray-200 dark:bg-gray-700 rounded" />
                  <div className="flex gap-3">
                    <div className="w-16 h-3 bg-gray-200 dark:bg-gray-700 rounded" />
                    <div className="w-16 h-3 bg-gray-200 dark:bg-gray-700 rounded" />
                    <div className="w-12 h-3 bg-gray-200 dark:bg-gray-700 rounded" />
                  </div>
                </div>
              ))}
              {/* Desktop skeleton rows — single table */}
              <div className="hidden md:table w-full table-fixed">
                <colgroup>
                  <col style={{ width: "280px" }} />
                  <col style={{ width: "96px" }} />
                  <col style={{ width: "56px" }} />
                  <col style={{ width: "72px" }} />
                  <col style={{ width: "48px" }} />
                  <col style={{ width: "56px" }} />
                  <col style={{ width: "80px" }} />
                  <col style={{ width: "64px" }} />
                </colgroup>
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="hidden md:table-row py-3 px-4 border-b border-gray-100 dark:border-gray-700/50 last:border-b-0">
                    <span className="hidden md:table-cell shrink-0" style={{ width: "280px" }}>
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded" />
                    </span>
                    <span className="hidden md:table-cell shrink-0" style={{ width: "96px" }}>
                      <div className="flex items-center gap-1.5 h-4">
                        <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded" />
                        <div className="w-10 h-3 bg-gray-200 dark:bg-gray-700 rounded" />
                      </div>
                    </span>
                    <span className="hidden md:table-cell shrink-0" style={{ width: "56px" }}>
                      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded" />
                    </span>
                    <span className="hidden md:table-cell shrink-0" style={{ width: "72px" }}>
                      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded" />
                    </span>
                    <span className="hidden md:table-cell shrink-0" style={{ width: "48px" }}>
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded" />
                    </span>
                    <span className="hidden md:table-cell shrink-0" style={{ width: "56px" }}>
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded" />
                    </span>
                    <span className="hidden md:table-cell shrink-0" style={{ width: "80px" }}>
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded" />
                    </span>
                    <span className="hidden md:table-cell shrink-0" style={{ width: "64px" }}>
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded" />
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : attractions?.length === 0 ? (
            <EmptyState icon={Search} iconColor="text-gray-400"
              title="No attractions found"
              subtitle="Adjust your filters" />
          ) : (
            <div className="md:table w-full table-fixed">
              <colgroup>
                <col style={{ width: "280px" }} />
                <col style={{ width: "96px" }} />
                <col style={{ width: "56px" }} />
                <col style={{ width: "72px" }} />
                <col style={{ width: "48px" }} />
                <col style={{ width: "56px" }} />
                <col style={{ width: "80px" }} />
                <col style={{ width: "64px" }} />
              </colgroup>
              {attractions?.map((attraction: AdminAttraction) => (
                <>
                  {/* Desktop row */}
                  <div key={attraction.id}
                    className="hidden md:table-row py-3 px-4 border-b border-gray-100 dark:border-gray-700/50 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
                    {/* Name — FIXED width, never resizes */}
                    <span className="hidden md:table-cell shrink-0" style={{ width: "280px" }}>
                      <span className="block truncate text-sm text-gray-800 dark:text-gray-200 font-medium leading-tight">{attraction.name}</span>
                    </span>
                    {/* Park — icon + abbreviation */}
                    <span className="hidden md:table-cell shrink-0" style={{ width: "96px" }}>
                      <span className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                        {React.createElement(getParkIcon(attraction.parkName), { className: "w-5 h-5" })}
                        <span className="truncate">{getParkAbbr(attraction.parkName)}</span>
                      </span>
                    </span>
                    {/* EntityType */}
                    <span className="hidden md:table-cell shrink-0" style={{ width: "56px" }}>
                      <EntityTypePill entityType={attraction.entityType} />
                    </span>
                    {/* Status — color-coded pill */}
                    <span className="hidden md:table-cell shrink-0" style={{ width: "72px" }}>
                      <StatusPill status={attraction.queueStatus} />
                    </span>
                    {/* Rating */}
                    <span className="hidden md:table-cell shrink-0" style={{ width: "48px" }}>
                      {attraction.rating != null && attraction.rating > 0 ? (
                        <span className="flex items-center justify-end gap-0.5">
                          <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                          <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">{attraction.rating}</span>
                        </span>
                      ) : (
                        <span className="block text-sm text-gray-300 dark:text-gray-600 text-right">—</span>
                      )}
                    </span>
                    {/* Priority */}
                    <span className="hidden md:table-cell shrink-0" style={{ width: "56px" }}>
                      <PriorityBadge priority={attraction.priority ?? 'MEDIUM'} />
                    </span>
                    {/* Wait Time — penultimate column */}
                    <span className="hidden md:table-cell shrink-0" style={{ width: "80px" }}>
                      <WaitTimeIndicator
                          minutes={attraction.queueStatus?.toLowerCase() === 'operating' ? (attraction.waitTimeMinutes ?? null) : null}
                          attractionId={attraction.id}
                          parkRank={parkRanks[attraction.id]}
                          parkTotal={operatingGroups[attraction.parkName]?.length ?? null}
                        />
                    </span>
                    {/* Last Update — last column */}
                    <span className="hidden md:table-cell shrink-0" style={{ width: "64px" }}>
                      <AttractionFreshness minutesSinceUpdate={attraction.minutesSinceUpdate} />
                    </span>
                  </div>

                  {/* Mobile card */}
                  <div className="md:hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-card p-3 space-y-2" key={`card-${attraction.id}`}>
                    {/* Line 1: Attraction name */}
                    <span className="min-w-0 line-clamp-2 text-base text-gray-900 dark:text-gray-100 font-semibold leading-tight">
                      {attraction.name}
                    </span>
                    {/* Line 2: Park icon + type */}
                    <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                      {React.createElement(getParkIcon(attraction.parkName), { className: "w-4 h-4" })}
                      <span className="truncate">{getParkAbbr(attraction.parkName)}</span>
                      <span className="ml-auto">
                        <EntityTypePill entityType={attraction.entityType} />
                      </span>
                    </div>
                    {/* Line 3: Metrics */}
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-3">
                        <StatusPill status={attraction.queueStatus} />
                        <WaitTimeIndicator
                          minutes={attraction.queueStatus?.toLowerCase() === 'operating' ? (attraction.waitTimeMinutes ?? null) : null}
                          attractionId={attraction.id}
                          parkRank={parkRanks[attraction.id]}
                          parkTotal={operatingGroups[attraction.parkName]?.length ?? null}
                        />
                        {attraction.rating != null && attraction.rating > 0 ? (
                          <span className="flex items-center gap-0.5">
                            <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                            <span className="font-semibold">{attraction.rating}</span>
                          </span>
                        ) : null}
                        <PriorityBadge priority={attraction.priority ?? 'MEDIUM'} />
                      </div>
                      <AttractionFreshness minutesSinceUpdate={attraction.minutesSinceUpdate} />
                    </div>
                  </div>
                </>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
